import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Sidebar } from 'primereact/sidebar';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../../Layout/Layout';
import GridPreview from './components/GridPreview';
import { Dialog } from 'primereact/dialog';
import { useGetPlaylist, useUpdatePlaylist } from '../../../queries/wallPlaylistLibrary';
import { useGetWallMediaLibrary } from '../../../queries/videoWall';
import { useGetTemplates, useGetThemes } from '../../../queries/signageTemplates';
import PlaylistPreviewPlayer from './preview/PlaylistPreviewPlayer';
import useEditorDirty from './useEditorDirty';
import LiveContentBanner from './components/LiveContentBanner';

// 'template' enables HYBRID playlists — mix native menu/special slides with
// full-wall promo videos in one playlist (Phase 6). Time-of-day switching is the
// existing Assignments/Schedule layer; nothing new on the sync path.
const CONTENT_TYPES = ['full_wall', 'media_file', 'split', 'template'];
const FIT_MODES = ['letterbox', 'crop', 'stretch'];
const TYPE_ICON = { full_wall: 'pi pi-window-maximize', media_file: 'pi pi-image', split: 'pi pi-th-large', template: 'pi pi-palette' };

let KEY_SEQ = 1;
const nextKey = () => `it_${KEY_SEQ++}`;

// Format seconds → "m:ss" for the read-only video duration label.
const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// The persisted item shape — SINGLE source of truth shared by save() and the
// dirty-state serializer so the Save button enables iff a save would change data.
const buildItemsPayload = (items) => items.map((it) => ({
  content_type: it.content_type,
  fit_mode: it.fit_mode || 'letterbox',
  media_file_id: (it.content_type === 'split' || it.content_type === 'template') ? null : (it.media_file_id || null),
  template_instance_id: it.content_type === 'template' ? (it.template_instance_id || null) : null,
  file_url: (it.content_type === 'split' || it.content_type === 'template') ? null : (it.file_url || null),
  duration_seconds: it.duration_seconds || 1,
  screen_assignments: it.content_type === 'split' ? (it.screen_assignments || {}) : null,
}));
const serializeContent = (name, description, items) => JSON.stringify({
  name: (name || '').trim(),
  description: (description || '').trim() || null,
  items: buildItemsPayload(items),
});

/** Reconstruct an editing grid for a split item from its saved positions. */
function deriveGrid(assignments) {
  const positions = Object.keys(assignments || {}).map(Number).filter((n) => n > 0);
  const max = positions.length ? Math.max(...positions) : 0;
  if (max <= 0) return { rows: 2, cols: 2 };
  const cols = Math.ceil(Math.sqrt(max));
  const rows = Math.ceil(max / cols);
  return { rows, cols };
}

/**
 * Content Library — full-page playlist editor (Phase 2 of the restaurant-chain
 * platform redesign, .claude/PLATFORM_DESIGN.md). Replaces the cramped
 * modal-in-modal editor with: drag-to-reorder item cards, visual thumbnails,
 * a compact duration field, fit-mode chips, and an inline media drawer (no nested
 * modals). Pure UI — saves the same item payload; the sync engine is not involved
 * (library playlists never reach RTDB until assigned to a wall in Phase 3).
 */
export default function VideoWallLibraryEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { playlistId } = useParams();

  const { data: playlist, isLoading } = useGetPlaylist(playlistId);
  const update = useUpdatePlaylist();
  const { data: mediaRaw } = useGetWallMediaLibrary(true);
  const { data: templateList = [] } = useGetTemplates();
  const { data: themeData } = useGetThemes();
  const themes = themeData?.themes ?? [];
  const defaultZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const [previewing, setPreviewing] = useState(false);

  const mediaList = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [mediaRaw]);
  const mediaMap = useMemo(() => {
    const m = {};
    mediaList.forEach((x) => { m[x.id] = x; });
    return m;
  }, [mediaList]);
  const resolvePhoto = useMemo(() => (id) => mediaMap[id]?.path || mediaMap[id]?.thumb || null, [mediaMap]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [items, setItems] = useState([]);
  const [picker, setPicker] = useState(null); // { key } or { key, position }
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const { markClean, isDirty } = useEditorDirty();
  // Walls/groups currently playing THIS playlist, returned by the save endpoint.
  const [affected, setAffected] = useState([]);

  useEffect(() => {
    if (!playlist) return;
    const nm = playlist.name || '';
    const desc = playlist.description || '';
    const its = (playlist.items || []).map((it) => {
      const screen_assignments = it.screen_assignments || {};
      const grid = deriveGrid(screen_assignments);
      return {
        _key: nextKey(),
        content_type: it.content_type || 'full_wall',
        fit_mode: it.fit_mode || 'letterbox',
        media_file_id: it.media_file_id || null,
        template_instance_id: it.template_instance_id || null,
        file_url: it.file_url || null,
        duration_seconds: it.duration_seconds || 10,
        screen_assignments,
        _gridRows: grid.rows,
        _gridCols: grid.cols,
      };
    });
    setName(nm);
    setDescription(desc);
    setItems(its);
    markClean(serializeContent(nm, desc, its)); // freshly loaded == clean
  }, [playlist, markClean]);

  /* ── item mutations (by stable _key so drag/reorder never mis-targets) ── */
  const patch = (key, p) => setItems((arr) => arr.map((it) => (it._key === key ? { ...it, ...p } : it)));
  const removeItem = (key) => setItems((arr) => arr.filter((it) => it._key !== key));
  const addItem = () => setItems((arr) => [...arr, {
    _key: nextKey(), content_type: 'full_wall', fit_mode: 'letterbox',
    media_file_id: null, template_instance_id: null, file_url: null, duration_seconds: 10,
    screen_assignments: {}, _gridRows: 2, _gridCols: 2,
  }]);

  const reorder = (from, to) => setItems((arr) => {
    if (from == null || to == null || from === to) return arr;
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  });

  /* ── media picker (inline drawer) ── */
  const onPickMedia = (m) => {
    if (!picker) return;
    if (picker.position != null) {
      const it = items.find((x) => x._key === picker.key);
      patch(picker.key, { screen_assignments: { ...(it?.screen_assignments || {}), [picker.position]: m.id } });
    } else {
      // Default the slot to the VIDEO's real length (media.duration is seconds);
      // images keep their existing default. The user can still adjust it after.
      const vidDur = m.type === 'VIDEO' && m.duration > 0 ? { duration_seconds: Math.max(1, Math.round(m.duration)) } : {};
      patch(picker.key, { media_file_id: m.id, file_url: null, ...vidDur });
    }
    setPicker(null);
  };

  const serialized = useMemo(() => serializeContent(name, description, items), [name, description, items]);
  const dirty = isDirty(serialized);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const res = await update.mutateAsync({
      id: playlistId,
      name: trimmed,
      description: description.trim() || null,
      items: buildItemsPayload(items),
    });
    markClean(serialized); // saved → button disables again
    // Backend reports walls/groups currently PLAYING this playlist so we can offer
    // a one-click Refresh (saving never auto-interrupts live screens).
    const body = res?.data?.data ?? res?.data ?? res;
    setAffected(Array.isArray(body?.affected_walls) ? body.affected_walls : []);
  };

  const mediaThumb = (id) => mediaMap[id]?.thumb || mediaMap[id]?.path || null;

  // Duration policy: a VIDEO item plays its REAL file length (backend ignores the
  // slot), so the field is read-only when we KNOW that length. We only know it for a
  // selected media row of type VIDEO; a raw pasted file_url video stays editable
  // (length unknown). split/template keep the editable slot. Returns real seconds or null.
  const lockedVideoSecs = (it) => {
    if (it.content_type !== 'full_wall' && it.content_type !== 'media_file') return null;
    const m = it.media_file_id ? mediaMap[it.media_file_id] : null;
    return m && m.type === 'VIDEO' && m.duration > 0 ? m.duration : null;
  };

  if (isLoading) {
    return (
      <Layout>
        <Skeleton height="3rem" className="mb-3" />
        <Skeleton height="20rem" />
      </Layout>
    );
  }

  if (!playlist) {
    return (
      <Layout>
        <div className="text-center p-5" style={{ color: 'var(--pp-text-secondary)' }}>
          {t('video_wall.library.not_found')}
          <div className="mt-3">
            <Button label={t('video_wall.library.back')} icon="pi pi-arrow-left"
              onClick={() => navigate('/apps/video-wall/library')} />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Sticky header */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 pb-3"
        style={{ borderBottom: '1px solid var(--pp-border)' }}
      >
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
          <Button icon="pi pi-arrow-left" rounded text aria-label={t('video_wall.library.back')}
            onClick={() => navigate('/apps/video-wall/library')} />
          <div style={{ minWidth: 0 }}>
            <InputText
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={150}
              placeholder={t('video_wall.library.name_ph')}
              style={{ fontWeight: 700, fontSize: 18, border: 'none', background: 'transparent',
                color: 'var(--pp-text-primary)', padding: 0, width: '100%' }}
            />
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              <i className="pi pi-info-circle me-1" />
              {t('video_wall.library.not_assigned_hint')}
            </small>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button label={t('video_wall.preview.play_playlist')} icon="pi pi-play" outlined
            onClick={() => setPreviewing(true)} disabled={!items.length} />
          <Button label={t('video_wall.save')} icon="pi pi-check" loading={update.isLoading}
            onClick={save} disabled={!name.trim() || !dirty} />
        </div>
      </div>

      {/* Walls/groups playing this playlist right now — one-click Refresh to apply. */}
      <LiveContentBanner walls={affected} onDismiss={() => setAffected([])} />

      <Dialog visible={previewing} onHide={() => setPreviewing(false)} header={t('video_wall.preview.play_playlist')}
        style={{ width: 'min(96vw, 720px)' }} dismissableMask>
        <PlaylistPreviewPlayer items={items} mediaMap={mediaMap} themes={themes}
          defaultZones={defaultZones} resolvePhoto={resolvePhoto} maxWidth={560} />
      </Dialog>

      {/* Description */}
      <div className="mb-4" style={{ maxWidth: 640 }}>
        <InputTextarea
          className="w-100"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('video_wall.library.description_ph')}
          rows={2}
          maxLength={500}
          autoResize
        />
      </div>

      {/* Items */}
      {items.length === 0 ? (
        <div
          className="text-center p-5 mb-3"
          style={{ background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border)',
            borderRadius: 'var(--pp-radius-lg)' }}
        >
          <div style={{ fontSize: '2.5rem' }}>🎬</div>
          <p className="mt-2 mb-3" style={{ color: 'var(--pp-text-secondary)' }}>
            {t('video_wall.library.empty_items')}
          </p>
          <Button label={t('video_wall.add_content')} icon="pi pi-plus" onClick={addItem} />
        </div>
      ) : (
        <div className="d-flex flex-column gap-2 mb-3">
          {items.map((it, idx) => (
            <div
              key={it._key}
              onDragOver={(e) => { e.preventDefault(); if (dragOver !== idx) setDragOver(idx); }}
              onDrop={(e) => { e.preventDefault(); reorder(dragIndex.current, idx); dragIndex.current = null; setDragOver(null); }}
              className="p-3 d-flex align-items-start gap-3"
              style={{
                background: 'var(--pp-bg-elevated)',
                border: `1px solid ${dragOver === idx ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`,
                borderRadius: 'var(--pp-radius-md)',
                boxShadow: 'var(--pp-shadow-sm)',
              }}
            >
              {/* Drag handle (only the handle is draggable so inputs work normally) */}
              <span
                draggable
                onDragStart={(e) => { dragIndex.current = idx; e.dataTransfer.effectAllowed = 'move'; }}
                onDragEnd={() => { dragIndex.current = null; setDragOver(null); }}
                title={t('video_wall.library.drag_hint')}
                style={{ cursor: 'grab', color: 'var(--pp-text-secondary)', paddingTop: 4 }}
              >
                <i className="pi pi-bars" />
              </span>

              {/* Thumbnail / preview */}
              <div style={{ width: 96, flexShrink: 0 }}>
                {it.content_type === 'split' ? (
                  <GridPreview
                    rows={it._gridRows} columns={it._gridCols} orientation="landscape" maxWidth={96}
                    renderCell={(r, c, pos) => {
                      const tb = mediaThumb(it.screen_assignments?.[pos]);
                      return tb
                        ? <img src={tb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 10 }}>{pos}</span>;
                    }}
                  />
                ) : (it.media_file_id && mediaThumb(it.media_file_id)) ? (
                  <img src={mediaThumb(it.media_file_id)} alt=""
                    style={{ width: 96, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--pp-border)' }} />
                ) : (
                  <div style={{ width: 96, height: 60, borderRadius: 8, border: '1px dashed var(--pp-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--pp-text-secondary)' }}>
                    <i className={TYPE_ICON[it.content_type]} style={{ fontSize: 20 }} />
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <span style={{ color: 'var(--pp-text-secondary)', fontWeight: 700, width: 24 }}>#{idx + 1}</span>
                  <Dropdown
                    value={it.content_type}
                    options={CONTENT_TYPES.map((c) => ({ label: t(`video_wall.ct_${c}`), value: c }))}
                    onChange={(e) => patch(it._key, { content_type: e.value })}
                    style={{ minWidth: 190 }}
                  />
                  {(() => {
                    const locked = lockedVideoSecs(it);
                    return locked != null ? (
                      <span className="d-inline-flex align-items-center gap-1" title={t('video_wall.library.video_duration_locked')}>
                        <i className="pi pi-lock" style={{ fontSize: 12, color: 'var(--pp-text-secondary)' }} />
                        <span style={{ color: 'var(--pp-text-secondary)' }}>{fmtDuration(locked)}</span>
                        <i className="pi pi-info-circle" style={{ fontSize: 12, color: 'var(--pp-text-secondary)' }}
                          title={t('video_wall.library.video_duration_locked')} />
                      </span>
                    ) : (
                      <span className="d-inline-flex align-items-center gap-1">
                        <InputNumber
                          value={it.duration_seconds} min={1} max={86400} useGrouping={false}
                          onValueChange={(e) => patch(it._key, { duration_seconds: e.value || 1 })}
                          inputStyle={{ width: 70 }}
                        />
                        <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.library.sec')}</small>
                      </span>
                    );
                  })()}
                  {it.content_type === 'full_wall' && (
                    <Dropdown
                      value={it.fit_mode || 'letterbox'}
                      options={FIT_MODES.map((f) => ({ label: t(`video_wall.fit_${f}`), value: f }))}
                      onChange={(e) => patch(it._key, { fit_mode: e.value })}
                      style={{ minWidth: 130 }}
                    />
                  )}
                  <Button icon="pi pi-trash" rounded text severity="danger" className="ms-auto"
                    aria-label={t('video_wall.library.delete')} onClick={() => removeItem(it._key)} />
                </div>

                {/* Per-type body */}
                {it.content_type === 'template' ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Dropdown
                      value={it.template_instance_id}
                      options={templateList.map((tpl) => ({ label: tpl.name, value: tpl.id }))}
                      onChange={(e) => patch(it._key, { template_instance_id: e.value })}
                      placeholder={t('video_wall.templates.pick_template')}
                      filter showClear style={{ minWidth: 240 }} />
                    <Button text size="small" icon="pi pi-external-link"
                      label={t('video_wall.templates.title')} onClick={() => navigate('/apps/video-wall/templates')} />
                  </div>
                ) : it.content_type !== 'split' ? (
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button label={t('video_wall.choose_media')} icon="pi pi-images" size="small" outlined
                      onClick={() => setPicker({ key: it._key })} />
                    <span className="small text-truncate" style={{ color: 'var(--pp-text-secondary)', maxWidth: 200 }}>
                      {it.media_file_id ? (mediaMap[it.media_file_id]?.file_name || t('video_wall.media_selected')) : ''}
                    </span>
                    <InputText value={it.file_url || ''} placeholder={t('video_wall.or_paste_url')}
                      onChange={(e) => patch(it._key, { file_url: e.target.value || null, media_file_id: null })}
                      className="flex-grow-1" style={{ minWidth: 160 }} />
                  </div>
                ) : (
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                      <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.library.split_grid')}</small>
                      <InputNumber value={it._gridRows} min={1} max={6} showButtons buttonLayout="horizontal"
                        decrementButtonClassName="p-button-text" incrementButtonClassName="p-button-text"
                        onValueChange={(e) => patch(it._key, { _gridRows: e.value || 1 })} inputStyle={{ width: 44 }} />
                      <span style={{ color: 'var(--pp-text-secondary)' }}>×</span>
                      <InputNumber value={it._gridCols} min={1} max={6} showButtons buttonLayout="horizontal"
                        decrementButtonClassName="p-button-text" incrementButtonClassName="p-button-text"
                        onValueChange={(e) => patch(it._key, { _gridCols: e.value || 1 })} inputStyle={{ width: 44 }} />
                    </div>
                    <small className="d-block mb-2" style={{ color: 'var(--pp-text-secondary)' }}>
                      {t('video_wall.library.split_cell_hint')}
                    </small>
                    <GridPreview
                      rows={it._gridRows} columns={it._gridCols} orientation="landscape" maxWidth={240}
                      renderCell={(r, c, pos) => {
                        const tb = mediaThumb(it.screen_assignments?.[pos]);
                        return (
                          <div
                            onClick={() => setPicker({ key: it._key, position: pos })}
                            style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title={t('video_wall.library.assign_cell', { n: pos })}
                          >
                            {tb
                              ? <img src={tb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <span style={{ fontSize: 11 }}>{pos}</span>}
                          </div>
                        );
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <Button label={t('video_wall.add_content')} icon="pi pi-plus" text onClick={addItem} className="mb-4" />
      )}

      {/* Inline media drawer (replaces the old nested modal) */}
      <Sidebar visible={!!picker} position="right" onHide={() => setPicker(null)}
        style={{ width: 'min(96vw, 460px)' }}>
        <h5 className="mb-3" style={{ color: 'var(--pp-text-primary)' }}>
          🎬 {picker?.position != null
            ? t('video_wall.library.assign_cell', { n: picker.position })
            : t('video_wall.pick_media')}
        </h5>
        {mediaList.length === 0 ? (
          <div className="p-4 text-center small" style={{ color: 'var(--pp-text-secondary)' }}>
            {t('video_wall.no_media')}
          </div>
        ) : (
          <div className="row g-2">
            {mediaList.map((m) => (
              <div key={m.id} className="col-6">
                <button type="button" className="p-0 border-0 w-100"
                  style={{ background: 'transparent', cursor: 'pointer' }}
                  onClick={() => onPickMedia(m)} title={m.file_name}>
                  <img src={m.thumb || m.path} alt={m.file_name} className="w-100"
                    style={{ height: 90, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--pp-border)' }} />
                  <small className="d-block text-truncate" style={{ color: 'var(--pp-text-secondary)' }}>
                    {m.type === 'VIDEO' ? '🎬 ' : '🖼️ '}{m.file_name}
                  </small>
                </button>
              </div>
            ))}
          </div>
        )}
      </Sidebar>
    </Layout>
  );
}
